import { z } from 'zod';
/** Limits enforced by the API; the web client mirrors them for early feedback. */
export const LIMITS = {
    maxFilesPerSubmission: 20,
    maxFileBytes: 2 * 1024 * 1024 * 1024, // 2 GiB
    maxSubmissionBytes: 8 * 1024 * 1024 * 1024, // 8 GiB
    minFileBytes: 1024,
    sasTtlMinutes: 60,
};
export const ErrorCode = z.enum([
    'VALIDATION',
    'TURNSTILE_FAILED',
    'RATE_LIMITED',
    'TOO_MANY_FILES',
    'FILE_TOO_LARGE',
    'FILE_TOO_SMALL',
    'NOT_PDF',
    'NOT_FOUND',
    'UNAUTHORIZED',
    'FILES_NOT_READY',
    'INVALID_EMAIL',
    'DISPOSABLE_EMAIL',
    'NO_FILES',
    'INTAKE_DISABLED',
    'INTERNAL',
]);
export const ApiError = z.object({
    code: ErrorCode,
    message: z.string(),
    details: z.unknown().optional(),
});
/** User-facing copy for the errors the page renders; the API returns these exact strings. */
export const ERROR_MESSAGES = {
    VALIDATION: 'Something in the request was not right. Reload the page and try again.',
    TURNSTILE_FAILED: 'We could not confirm you are human. Reload the page and try again.',
    RATE_LIMITED: 'Too many submissions from this connection. Try again in a while.',
    TOO_MANY_FILES: `Up to ${LIMITS.maxFilesPerSubmission} manuals per submission.`,
    FILE_TOO_LARGE: 'That manual is larger than 2 GB. Split it and try again.',
    FILE_TOO_SMALL: 'That file looks empty.',
    NOT_PDF: 'Only PDF manuals are accepted.',
    NOT_FOUND: 'This submission is no longer available. Start again.',
    UNAUTHORIZED: 'This submission is no longer available. Start again.',
    FILES_NOT_READY: 'Wait for the uploads to finish, then send.',
    INVALID_EMAIL: 'Enter the work email we should send your registration link to.',
    DISPOSABLE_EMAIL: 'Use a work email; temporary addresses cannot receive the registration link.',
    NO_FILES: 'Add at least one manual before sending.',
    INTAKE_DISABLED: 'Uploads are paused for a moment. Please try again later.',
    INTERNAL: 'Something went wrong on our side. Try again in a moment.',
};
export const FileDeclaration = z.object({
    clientId: z.string().min(1).max(64),
    name: z.string().min(1).max(255),
    sizeBytes: z.number().int().positive(),
    contentType: z.string().max(100).optional(),
});
export const CreateSubmissionRequest = z.object({
    turnstileToken: z.string().max(4096).optional(),
    website: z.string().max(200).optional(), // honeypot, must be empty
    files: z.array(FileDeclaration).min(1).max(LIMITS.maxFilesPerSubmission),
});
export const IssuedFile = z.object({
    clientId: z.string(),
    fileId: z.string(),
    blobName: z.string(),
    uploadUrl: z.string().url(),
    sasExpiresAt: z.string(),
});
export const CreateSubmissionResponse = z.object({
    submissionId: z.string(),
    submissionToken: z.string(),
    files: z.array(IssuedFile),
});
export const AddFilesRequest = z.object({ files: z.array(FileDeclaration).min(1).max(LIMITS.maxFilesPerSubmission) });
export const AddFilesResponse = z.object({ files: z.array(IssuedFile) });
export const RenewSasResponse = z.object({ uploadUrl: z.string().url(), sasExpiresAt: z.string() });
export const CompleteSubmissionRequest = z.object({
    email: z.string().min(3).max(254),
    locale: z.string().max(20).optional(),
});
export const CompleteSubmissionResponse = z.object({
    submissionId: z.string(),
    fileCount: z.number().int(),
    totalBytes: z.number().int(),
    receivedAt: z.string(),
});
export const FileProblemCode = z.enum(['MISSING', 'SIZE_MISMATCH', 'NOT_PDF', 'MALWARE']);
export const FilesNotReadyError = ApiError.extend({
    code: z.literal('FILES_NOT_READY'),
    files: z.array(z.object({ fileId: z.string(), code: FileProblemCode })),
});
export const MalwareScan = z.enum(['NoThreatsFound', 'MalwareDetected', 'NotScanned']);
export const ManifestFile = z.object({
    index: z.number().int().positive(),
    fileId: z.string(),
    blobName: z.string(),
    url: z.string().url(),
    originalName: z.string(),
    sizeBytes: z.number().int().positive(),
    contentType: z.literal('application/pdf'),
    etag: z.string(),
    malwareScan: MalwareScan,
});
/** Written once per submission at intake/{submissionId}/manifest.json; never modified. */
export const ManifestV1 = z.object({
    schemaVersion: z.literal(1),
    submissionId: z.string(),
    submittedAt: z.string(),
    source: z.string(),
    contact: z.object({ email: z.string(), locale: z.string().optional() }),
    files: z.array(ManifestFile).min(1),
    client: z.object({ ipHash: z.string(), userAgent: z.string() }),
});
export const PDF_MAGIC = '%PDF-';
//# sourceMappingURL=index.js.map